import { useCallback, useMemo } from 'react';
import type { FunctionComponent } from 'react';

import { nuc } from '../../../utils';
import SidebarNavLayout from '../../layouts/SidebarNavLayout';
import type { Project, RouterProps } from '../../../types';

import ProjectSettingsContextProvider from './context';
import {
  useCanUserEditProject,
  useCanUserSetSBOMPolicy,
  useCanUserSetSomeIssueFilter,
  useCanUserSetSomeIssuePolicy,
} from './permissions';

type ProjectSettingsPath = 'settings' | 'issues' | 'issue-tracker' | 'notifications' | 'update-hooks' | 'builds' | 'policies';

type Params = {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  project_id: string;
};

type Props = RouterProps<Params> & {
  project: {
    //  Note that this component will only render once project is loaded, which is why this is not optional.
    data: Project; //  Note that this includes associations, such as notifications, labels, etc.
  };
  onProjectChange: () => void;
};

/**
 * ProjectSettingsContainer wraps all the settings components. It consists of the
 * sidebar navigation and the content area. It also provides the context for the
 * settings pages to consume.
 */
const ProjectSettingsContainer: FunctionComponent<Props> = ({
  children,
  params,
  location,
  project,
  onProjectChange: onReloadProject,
}) => {
  const baseProjectURL = useMemo(() => `/projects/${nuc(params.project_id)}/settings`, [params.project_id]);
  const isPathActive = useCallback((pathPart: ProjectSettingsPath) => {
    const pathWithoutTrailingSlash = location.pathname.replace(/\/$/, '');
    return pathWithoutTrailingSlash.endsWith(pathPart);
  }, [location.pathname]);
  const canEdit = useCanUserEditProject(project.data?.locator ?? '');
  const canSetSomeIssuePolicy = useCanUserSetSomeIssuePolicy(project.data?.locator ?? '');
  const canSetSBOMPolicy = useCanUserSetSBOMPolicy(project.data?.locator ?? '');
  const canSetSomeIssueFilter = useCanUserSetSomeIssueFilter(project.data?.locator ?? '');
  const shouldShowPolicies = canEdit || canSetSomeIssuePolicy || canSetSBOMPolicy || canSetSomeIssueFilter;

  return (
    <SidebarNavLayout>
      <SidebarNavLayout.Nav>
        <SidebarNavLayout.NavHeading>
          Settings
        </SidebarNavLayout.NavHeading>
        <SidebarNavLayout.NavItem
          to={baseProjectURL}
          isActive={isPathActive('settings')}
        >
          General
        </SidebarNavLayout.NavItem>
        {shouldShowPolicies && (
          <SidebarNavLayout.NavItem
            to={`${baseProjectURL}/policies`}
            isActive={isPathActive('policies')}
          >
            Policies
          </SidebarNavLayout.NavItem>
        )}
        {/*
          Note that we are intentionally not using a Fragment here,
          because the SidebarNavLayout (and other components using the slots pattern)
          can only look at the direct children of the Nav component, meaning that
          if they are wrapped in a Fragment, they will not be rendered.
        */}
        {canEdit && (
          <SidebarNavLayout.NavItem
            to={`${baseProjectURL}/issue-tracker`}
            isActive={isPathActive('issue-tracker')}
          >
            Issue Tracker
          </SidebarNavLayout.NavItem>
        )}
        {canEdit && (
          <SidebarNavLayout.NavItem
            to={`${baseProjectURL}/notifications`}
            isActive={isPathActive('notifications')}
          >
            Notifications
          </SidebarNavLayout.NavItem>
        )}
        {canEdit && (
          <SidebarNavLayout.NavItem
            to={`${baseProjectURL}/update-hooks`}
            isActive={isPathActive('update-hooks')}
          >
            Hooks
          </SidebarNavLayout.NavItem>
        )}
        {canEdit && (
          <SidebarNavLayout.NavItem
            to={`${baseProjectURL}/builds`}
            isActive={isPathActive('builds')}
          >
            Builds
          </SidebarNavLayout.NavItem>
        )}
      </SidebarNavLayout.Nav>
      <SidebarNavLayout.Content>
        <ProjectSettingsContextProvider
          project={project.data}
          //  This calls up to the ProjectContainer component and tells it to "reload" in the Reflux store.
          //  This is helpful after a project has been saved, as the user may navigate to a different page of the
          //  project and will want the latest data.
          //  See `ProjectContainer.loadProject()` method, as that is what this is calling.
          onReloadProject={onReloadProject}
        >
          {children}
        </ProjectSettingsContextProvider>
      </SidebarNavLayout.Content>
    </SidebarNavLayout>
  );
};

export default ProjectSettingsContainer;
